import { useEffect } from 'react';
import Debug from 'debug';

import addItemToAccountAction from '~/app/lib/store/accounts/actions/addItemToAccountAction';
import addUserPageApi from '~/app/lib/songwhipApi/users/addUserPageApi';
import claimPageDialogLocales from '~/locales/fragments/claimPageDialog';
import useFetchSessionUser from '~/app/lib/hooks/useFetchSessionUser';
import { useAppToast } from '~/app/components/NextApp/lib/CoreUi';
import useSelectArtists from '~/app/lib/hooks/useSelectArtists';
import { useTracker } from '~/app/lib/tracker/useTracker';
import { ItemTypes } from '~/app/lib/songwhipApi/types';
import { useDispatch } from '~/app/lib/store/redux';
import { useI18nStatic } from '~/app/lib/i18n';

import { AlbumItemContext } from '../types';

const debug = Debug('songwhip/AlbumPage');

const useAutoClaimAlbum = (itemContext: AlbumItemContext) => {
  const dispatch = useDispatch();
  const showToast = useAppToast();
  const { trackEvent } = useTracker();
  const album = itemContext.data.item;
  const artists = useSelectArtists(album.artistIds);
  const user = useFetchSessionUser();

  const { t: tClaimPageDialog } = useI18nStatic<'claimPageDialog'>(
    claimPageDialogLocales
  );

  // Try to auto-claim the album page if the user already owns
  // one of the artist on the album
  // TODO: move this to external hook
  useEffect(() => {
    // we don't want to auto-claim prerelease pages
    if (!album.isPrerelease) {
      debug('Trying to auto-claim album');

      const userAccountIds = user.userAccounts?.map(({ id }) => id);

      const userOwnsAlbum = Boolean(
        userAccountIds &&
          album.ownedByAccountIds?.find((id) => {
            return userAccountIds.includes(id);
          })
      );

      if (userOwnsAlbum) {
        debug('Noop: The user already owns the album');
        return;
      }

      const userOwnsArtist = Boolean(
        userAccountIds &&
          artists?.find((artist) => {
            return artist.ownedByAccounts?.find(({ id }) => {
              return userAccountIds.includes(id);
            });
          })
      );

      if (!userOwnsArtist) {
        debug('Noop: The user does not own any of the artist on the album');
        return;
      }

      // We call `addUserPage` with `dryRun:true` to figure out
      // if the user can claim the page, if so we add the page to
      // the user's account that's able to claim it and show a
      // success toast, otherwise we silently ignore the errors.
      // The user will still be able to try to claim the page from
      // the action menu and see the reason why they cannot claim the page.
      addUserPageApi({
        userId: user.userId!,
        itemType: ItemTypes.ALBUM,
        itemId: album.id,
        dryRun: true,
      })
        .then(({ account }) => {
          dispatch(
            addItemToAccountAction({
              accountId: account.id,
              itemType: ItemTypes.ALBUM,
              itemId: album.id,
              trackEvent,
            })
          )
            .then(() => {
              debug('Album claimed');

              showToast({
                text: tClaimPageDialog('claimPageSuccess'),
                testId: 'claimPageSuccessToast',
              });
            })
            .catch((e) => {
              debug('Error claiming album', e);
            });
        })
        .catch((e) => {
          debug('User cannot claim album', e);
        });
    }
  }, [user, album, artists]);
};

export default useAutoClaimAlbum;
